import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { BaseModal } from '../../../../../../common/components/BaseModal/BaseModal';
import { useStore } from '../../../../../app/data/root.store';
import { getDocTypeIcon } from '../../../../assets/docsTypes/docTypes';
import { SmartTagIcon } from './SmartTagIcon';
import {
    AddCustomTagButton,
    AllTagsWrapper,
    CheckmarkIcon,
    CreateTagInput,
    CreateTagInputWrapper,
    CreateTagLabel,
    CreateTagSection,
    DocumentBadge,
    DocumentIcon,
    DocumentInfo,
    DocumentTitle,
    DocumentTypeHeader,
    PlusIcon,
    SaveButton,
    SaveButtonContainer,
    SelectedTag,
    SelectedTagContent,
    SelectedTagIcon,
    SelectedTagText,
    TagEditorModalBody,
    TagEditorModalClose,
    TagEditorModalHeader,
    TagEditorModalWrapper,
    TagOption,
    TagOptionContent,
    TagOptionIcon,
    TagOptionText,
    TagSection
} from './TagEditorModal.styles';
import { PREDEFINED_TAG_NAMES } from '../../../../data/vault-store';

export const TagEditorModal = observer(({
    isOpen,
    onClose,
    selectedTags = [],
    onSave,
}) => {
    const { vaultStore } = useStore();
    const [newTagInput, setNewTagInput] = useState('');
    const [localSelectedTags, setLocalSelectedTags] = useState([]);
    const [initiallySelectedTagIds, setInitiallySelectedTagIds] = useState(new Set(selectedTags.map(tag => tag.id)));
    const fileExtension = vaultStore.selectedFile?.originalName?.split('.').pop()?.toLowerCase();

    useEffect(() => {
        if (isOpen) {
            setLocalSelectedTags(selectedTags);
            setInitiallySelectedTagIds(new Set(selectedTags.map(tag => tag.id)));
        }
    }, [selectedTags, isOpen]);

    useEffect(() => {
        if (isOpen && vaultStore.userTags.length === 0) {
            vaultStore.fetchUserTags();
        }
    }, [isOpen, vaultStore]);

    // Get all tags with wasSelected property and sort them
    const allTagsWithStatus = useMemo(() => {
        // Use the store's allAvailableTags which includes predefined tags
        const tagsWithStatus = vaultStore.allAvailableTags.map(tag => ({
            ...tag,
            wasSelected: initiallySelectedTagIds.has(tag.id),
            isSelected: localSelectedTags.some(selectedTag => selectedTag.id === tag.id)
        }));

        // Add custom tags that were created (including those without isCustom flag)
        localSelectedTags.forEach(selectedTag => {
            const existsInAvailableTags = tagsWithStatus.some(tag => tag.id === selectedTag.id);
            const isCustomTag = selectedTag.isCustom || !vaultStore.allAvailableTags.some(tag => tag.id === selectedTag.id);

            if (isCustomTag && !existsInAvailableTags) {
                tagsWithStatus.push({
                    ...selectedTag,
                    isCustom: true,
                    wasSelected: initiallySelectedTagIds.has(selectedTag.id),
                    isSelected: true
                });
            }
        });

        // Sort so that newly created custom tags appear first, then initially selected tags, then alphabetically
        return tagsWithStatus.sort((a, b) => {
            // First priority: newly created custom tags (custom and not wasSelected)
            const aIsNewCustom = a.isCustom && !a.wasSelected;
            const bIsNewCustom = b.isCustom && !b.wasSelected;

            if (aIsNewCustom && !bIsNewCustom) return -1;
            if (!aIsNewCustom && bIsNewCustom) return 1;

            // Second priority: initially selected tags
            if (a.wasSelected && !b.wasSelected) return -1;
            if (!a.wasSelected && b.wasSelected) return 1;

            // Within the same group, sort alphabetically but keep 'other' last
            const aLabel = (a.label || a.id || '').toLowerCase();
            const bLabel = (b.label || b.id || '').toLowerCase();

            // Always put 'other' at the end of each group
            if (aLabel === 'other' && bLabel !== 'other') return 1;
            if (bLabel === 'other' && aLabel !== 'other') return -1;
            if (aLabel === 'other' && bLabel === 'other') return 0;

            // Normal alphabetical sort
            return aLabel.localeCompare(bLabel);
        });
    }, [vaultStore.allAvailableTags, localSelectedTags, initiallySelectedTagIds]);

    const renderTagOption = (tag) => {
        const isSelected = tag.isSelected;

        if (isSelected) {
            return (
                <SelectedTag key={tag.id} onClick={() => handleTagToggle(tag)}>
                    <SelectedTagContent>
                        <SelectedTagIcon>
                            <SmartTagIcon tagName={tag.id} size={24} />
                        </SelectedTagIcon>
                        <SelectedTagText>{PREDEFINED_TAG_NAMES[tag.id] || tag.label || tag.name}</SelectedTagText>
                        <CheckmarkIcon>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M7 12L10.5 15.5L17 9" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </CheckmarkIcon>
                    </SelectedTagContent>
                </SelectedTag>
            );
        } else {
            return (
                <TagOption key={tag.id} onClick={() => handleTagToggle(tag)}>
                    <TagOptionContent>
                        <TagOptionIcon>
                            <SmartTagIcon tagName={tag.id} size={24} />
                        </TagOptionIcon>
                        <TagOptionText>{PREDEFINED_TAG_NAMES[tag.id] || tag.label || tag.name}</TagOptionText>
                    </TagOptionContent>
                </TagOption>
            );
        }
    };

    if (!isOpen) return null;

    const handleTagToggle = (tag) => {
        const isSelected = localSelectedTags.some(t => t.id === tag.id);
        if (isSelected) {
            setLocalSelectedTags(localSelectedTags.filter(t => t.id !== tag.id));
        } else {
            setLocalSelectedTags([...localSelectedTags, tag]);
        }
    };

    const handleCreateTag = () => {
        if (newTagInput.trim()) {
            const customTag = {
                id: newTagInput.toLowerCase(),
                label: newTagInput,
                isCustom: true,
                wasSelected: false
            };

            const isAlreadySelected = localSelectedTags.some(tag =>
                tag.id.toLowerCase() === customTag.id.toLowerCase()
            );

            if (!isAlreadySelected) {
                setLocalSelectedTags([customTag, ...localSelectedTags]);
            }

            setNewTagInput('');
        }
    };

    const hasValidInput = newTagInput.trim().length > 0;

    const handleSave = () => {
        onSave(localSelectedTags);
        onClose();
    };

    return ReactDOM.createPortal(
        <BaseModal width="500px" height="620px" borderRadius={12} onBackDropClick={onClose}>
            <TagEditorModalWrapper>
                <TagEditorModalClose onClick={onClose}>
                    ×
                </TagEditorModalClose>

                <TagEditorModalHeader>
                    <DocumentTypeHeader>
                        <DocumentInfo>
                            <DocumentTitle>Document Type</DocumentTitle>
                            {localSelectedTags.length > 0 ? <DocumentBadge>{localSelectedTags.length}</DocumentBadge> : null}
                        </DocumentInfo>
                        <div style={{ width: '1px', height: '32px', background: '#DDE3EB' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <DocumentIcon>
                                {getDocTypeIcon(fileExtension)}
                            </DocumentIcon>
                            <DocumentTitle style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '250px' }}>{vaultStore.selectedFile.displayName}</DocumentTitle>
                        </div>
                    </DocumentTypeHeader>
                </TagEditorModalHeader>

                <TagEditorModalBody>
                    <CreateTagSection>
                        <CreateTagLabel>Create new tag for this file</CreateTagLabel>
                        <CreateTagInputWrapper>
                            {
                                (!hasValidInput) &&
                                <PlusIcon>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 5V19M5 12H19" stroke="#1A1B1D" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </PlusIcon>
                            }
                            <CreateTagInput
                                type="text"
                                placeholder="Create new type tag"
                                value={newTagInput}
                                autoFocus
                                onChange={(e) => setNewTagInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && hasValidInput && handleCreateTag()}
                                maxLength={48}
                            />
                            {hasValidInput && (
                                <AddCustomTagButton onClick={handleCreateTag}>
                                    Add new tag
                                </AddCustomTagButton>
                            )}
                        </CreateTagInputWrapper>
                    </CreateTagSection>

                    <TagSection>
                        <AllTagsWrapper>
                            {allTagsWithStatus.map(renderTagOption)}
                        </AllTagsWrapper>
                    </TagSection>
                </TagEditorModalBody>

                <SaveButtonContainer>
                    <SaveButton onClick={handleSave}>
                        Save tags
                    </SaveButton>
                </SaveButtonContainer>
            </TagEditorModalWrapper>
        </BaseModal>,
        document.body
    );
}); 